// SavedJobsPage.tsx
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  LocationOn,
} from '@mui/icons-material'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grow,
  Button,
  Tabs,
  Tab,
  Badge,
  Divider,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material'
import {
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Pending as PendingIcon,
  CheckCircle as ConvertedIcon,
  Cancel as RejectedIcon,
  Apps as AllIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Visibility as VisibilityIcon,
  Message as MessageIcon,
  LocalOffer as PackageIcon,
  InfoOutlined as InfoIcon,
  HealthAndSafety,
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import AdvancedPagination from '../components/pagination/AdvancedPagination'
import api from '../services/api'
import { ChatDialog } from '@/chat/components/ChatDialog'
import { useAppSelector } from '@/store'

type ProposalStatus = 'Active' | 'InActive'

type Proposal = {
  id: string
  jobId?: string
  title: string
  description: string
  budgetRange: string
  proposedRate: string
  servicePackageName: string
  appliedDate: string
  status: string
  patientName: string
  applicantsCount: number
  patientEmail?: string
  note?: string
  insuranceLabel?: string
  jobLocation?: string
  rawProposal?: any
  useInsurance?: boolean
  // optional fields for chat
  patientId?: any
  therapistId?: string
  patientAvatar?: string
}

const getStatusColor = (
  status: string
): 'warning' | 'success' | 'error' | 'default' => {
  return status === 'Active' ? 'success' : 'error'
}

const SavedJobsPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { user: authUser, isAuthenticated } = useAppSelector(
    state => state.auth
  )

  // therapistId from authUser
  const therapistId = authUser?.id ?? null

  const [activeTab, setActiveTab] = useState(0) // 0: Pending, 1: Converted, 2: Rejected, 3: All
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [savedJobs, setSavedJobs] = useState<Proposal[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [selectedSavedJob, setSelectedSavedJob] = useState<Proposal | null>(
    null
  )
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
  })
  const [chatDialogOpen, setChatDialogOpen] = useState(false)
  const [chatSavedJob, setChatSavedJob] = useState<Proposal | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')

  // Placeholder filters (unused for now)
  const patientIdFilter = ''
  const serviceIdFilter = ''

  const formatAppliedDate = (dateString?: string) => {
    if (!dateString) return 'Date coming soon'
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return 'Date coming soon'
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatCurrency = (value?: number | null) => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      })
    }
    return null
  }

  const formatModalityLabel = (modality: string): string => {
    return modality
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }
  const mapProposalResponse = (proposal: any): Proposal => {
    const jobDetails = proposal?.jobId || null
    const patientDetails = proposal?.jobId?.patientId || null
    const serviceDetails = proposal?.serviceId || null

    const patientLabel = (() => {
      if (!patientDetails) return 'Patient'
      const fullName = [patientDetails.firstName, patientDetails.lastName]
        .filter(Boolean)
        .join(' ')
        .trim()
      if (fullName) return fullName
      return patientDetails.email || 'Patient'
    })()

    const serviceName = proposal?.job_name || jobDetails?.serviceTitle || 'N/A'

    const locationLabel = (() => {
      if (!jobDetails) return 'N/A'
      const cityState = [jobDetails.city, jobDetails.state]
        .filter(Boolean)
        .join(', ')
        .trim()
      if (cityState) return cityState
      return 'N/A'
    })()

    const fallbackId =
      proposal?._id ||
      proposal?.id ||
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

    const budgetLabel = (() => {
      const privatePayBudgetLabel = formatCurrency(jobDetails?.privatePayBudget)
      if (privatePayBudgetLabel) {
        return `${privatePayBudgetLabel} / session`
      }
      return 'N/A'
    })()

    const proposedRateLabel = (() => {
      const privatePayRate = formatCurrency(
        serviceDetails?.private_pay_rate ??
          serviceDetails?.privatePayRate ??
          serviceDetails?.rate
      )
      if (privatePayRate) {
        return `${privatePayRate} / session`
      }
      return 'Rate details coming soon'
    })()

    const descriptionContent =
      jobDetails?.shortDescription || jobDetails?.detailedDescription || 'N/A'

    const applicantsCountValue =
      jobDetails?.applicant ??
      jobDetails?.totalApplicants ??
      jobDetails?.applicants?.length ??
      proposal?.applicantsCount ??
      0

    return {
      id: fallbackId,
      jobId: jobDetails?._id || proposal?.jobId || proposal?._id,
      title: serviceName,
      description: descriptionContent,
      budgetRange: budgetLabel,
      proposedRate: proposedRateLabel,
      servicePackageName:
        serviceDetails?.name || 'Service package details coming soon',
      appliedDate: formatAppliedDate(proposal?.createdAt),
      status: jobDetails?.status,
      patientName: patientLabel,
      applicantsCount: applicantsCountValue,
      patientEmail: patientDetails?.email,
      note: proposal?.note,
      jobLocation: locationLabel,
      insuranceLabel: jobDetails?.insuranceId?.insuranceName || 'N/A',
      rawProposal: proposal,
      useInsurance: jobDetails?.useInsurance ? true : false,
    }
  }

  const statusFilter: string | undefined =
    activeTab === 0
      ? undefined // ALL — no filter
      : activeTab === 1
        ? 'Active'
        : activeTab === 2
          ? 'Closed'
          : undefined

  const fetchSavedJobs = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const params: Record<string, string | number> = {
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter,
      }

      if (searchQuery?.trim()) {
        params.search = searchQuery.trim()
      }

      if (!therapistId) {
        setSavedJobs([])
        setTotalRecords(0)
        setLoading(false)
        return
      }

      const response = await api.get(`/therapist-saved/${therapistId}`, {
        params,
      })
      const responseData = response.data || {}

      const savedjobsArray = Array.isArray(responseData.jobs)
        ? responseData.jobs
        : Array.isArray(responseData.data?.jobs)
          ? responseData.data.jobs
          : Array.isArray(responseData.data)
            ? responseData.data
            : []

      const mapped = savedjobsArray.map(mapProposalResponse)

      const total =
        typeof responseData.totalRecords === 'number'
          ? responseData.totalRecords
          : typeof responseData.data?.totalRecords === 'number'
            ? responseData.data.totalRecords
            : typeof responseData.meta?.totalRecords === 'number'
              ? responseData.meta.totalRecords
              : mapped.length

      setSavedJobs(mapped)
      setTotalRecords(total)
    } catch (fetchError) {
      console.error('Failed to load saved jobs', fetchError)
      setError('Unable to load saved jobs right now. Please try again later.')
      setSavedJobs([])
      setTotalRecords(0)
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, searchQuery, activeTab, therapistId])

  useEffect(() => {
    fetchSavedJobs()
  }, [fetchSavedJobs])

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
    setPage(0)
  }

  const handleViewJob = (savedJob: Proposal) => {
    if (!savedJob.jobId) {
      console.warn('Job ID not available for this saved job yet.', savedJob)
      return
    }

    navigate(`/job-details/${savedJob.jobId}`, {
      state: { fromPage: 'saved-jobs' },
    })
  }

  const handleOpenDetailsDialog = (savedJob: Proposal) => {
    setSelectedSavedJob(savedJob)
    setDetailsDialogOpen(true)
  }

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false)
    setSelectedSavedJob(null)
  }
  const clearFilters = () => {
    setSearchQuery('')
  }
  const handleSendMessageClick = (savedJob: Proposal) => {
    if (!isAuthenticated || !authUser?.id) {
      setSnackbarState({
        open: true,
        message: 'Please log in to send messages.',
      })
      navigate('/provider-login')
      return
    }

    // Extract patient ID from jobId field
    const jobIdData = savedJob?.rawProposal?.jobId || null
    if (!jobIdData) {
      setSnackbarState({
        open: true,
        message: 'Unable to start chat. Job information is missing.',
      })
      return
    }

    // Get patient ID from jobId.patientId
    const patientIdFromJob = jobIdData.patientId || null
    if (!patientIdFromJob) {
      setSnackbarState({
        open: true,
        message: 'Unable to start chat. Patient information is missing.',
      })
      return
    }

    // Handle patient ID - can be string or object
    let patientId:
      | string
      | {
          _id: string
          firstName?: string
          lastName?: string
          profilePhotoUrl?: string
        } = ''

    if (typeof patientIdFromJob === 'string') {
      patientId = patientIdFromJob
    } else if (
      typeof patientIdFromJob === 'object' &&
      patientIdFromJob !== null
    ) {
      const patientIdValue = patientIdFromJob._id || patientIdFromJob.id
      if (patientIdValue) {
        patientId = {
          ...patientIdFromJob,
          _id: patientIdValue,
        } as {
          _id: string
          firstName?: string
          lastName?: string
          profilePhotoUrl?: string
        }
      } else {
        setSnackbarState({
          open: true,
          message: 'Unable to start chat. Patient ID is missing.',
        })
        return
      }
    }

    // Get therapist ID (current user)
    const therapistIdCurrent = authUser.id

    if (!patientId || !therapistIdCurrent) {
      setSnackbarState({
        open: true,
        message: 'Unable to start chat. Missing participant information.',
      })
      return
    }

    // Extract patient display info
    // Try to get from jobId if it's an object, otherwise use savedJob.patientName
    let patientName = savedJob.patientName || 'Patient'
    let patientAvatar: string | undefined = undefined

    if (
      typeof patientIdFromJob === 'object' &&
      patientIdFromJob !== null &&
      'firstName' in patientIdFromJob
    ) {
      const first = (patientIdFromJob as any).firstName?.trim() ?? ''
      const last = (patientIdFromJob as any).lastName?.trim() ?? ''
      const combined = `${first} ${last}`.trim()
      if (combined) {
        patientName = combined
      }
      patientAvatar = (patientIdFromJob as any).profilePhotoUrl
    }

    // Set chat saved job with all needed info
    setChatSavedJob({
      ...savedJob,
      patientId,
      therapistId: therapistIdCurrent,
      patientName,
      patientAvatar,
    } as Proposal & {
      patientId: typeof patientId
      therapistId: string
      patientName: string
      patientAvatar?: string
    })
    setChatDialogOpen(true)
  }

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') return
    setSnackbarState(prev => ({ ...prev, open: false }))
  }

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = parseInt(event.target.value, 10)
    setRowsPerPage(value)
    setPage(0)
  }

  const renderCardView = () => (
    <Grid container spacing={3}>
      {savedJobs.map((savedJob, index) => (
        <Grow
          in
          key={savedJob.id}
          timeout={300 + index * 100}
          style={{ transformOrigin: '0 0 0' }}
        >
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}
            >
              <CardContent>
                {/* Title and Status */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="start"
                  mb={2}
                >
                  <Typography variant="h6" component="div" fontWeight={600}>
                    {savedJob.title}
                  </Typography>
                  <Chip
                    label={savedJob.status}
                    color={getStatusColor(savedJob.status)}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>

                {/* Description */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {savedJob.description}
                </Typography>

                <Divider sx={{ my: 2 }} />

                {/* Patient Info */}
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {savedJob.patientName}
                  </Typography>
                </Box>

                {/* Budget / Insurance */}
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  {savedJob.useInsurance ? (
                    <HealthAndSafety fontSize="small" color="action" />
                  ) : (
                    <MoneyIcon fontSize="small" color="action" />
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {savedJob.useInsurance ? 'Insurance:' : 'Out of Pocket:'}{' '}
                    <strong>
                      {savedJob.useInsurance
                        ? savedJob.insuranceLabel
                        : savedJob.budgetRange}
                    </strong>
                  </Typography>
                </Box>

                {/* Patient Info */}
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <LocationOn fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {savedJob.jobLocation}
                  </Typography>
                </Box>

                {/* Your Proposal */}
                {/* <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <PackageIcon fontSize="small" color="action" />
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="primary"
                    >
                      {savedJob.proposedRate}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {savedJob.servicePackageName}
                    </Typography>
                  </Box>
                </Box> */}

                {/* Applied Date */}
                {/* <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Applied {savedJob.appliedDate}
                  </Typography>
                </Box> */}

                {/* Applicants Count */}
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <PeopleIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {savedJob.applicantsCount} applicant
                    {savedJob.applicantsCount === 1 ? '' : 's'} for this job
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Action Buttons */}
                <Box display="flex" gap={1.5}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewJob(savedJob)}
                    disabled={!savedJob.jobId}
                    fullWidth
                  >
                    View Job
                  </Button>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Tooltip placement="top" title="Message patient">
                      <IconButton
                        color="primary"
                        onClick={() => handleSendMessageClick(savedJob)}
                      >
                        <MessageIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grow>
      ))}
    </Grid>
  )

  const renderTableView = () => (
    <Grow in timeout={300}>
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Job Title
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Patient
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Budget
                </Typography>
              </TableCell>
              {/* <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Your Proposal
                </Typography>
              </TableCell> */}
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Applicants
                </Typography>
              </TableCell>
              {/* <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Applied Date
                </Typography>
              </TableCell> */}
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Status
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {savedJobs.map((savedJob, index) => (
              <Grow in key={savedJob.id} timeout={200 + index * 50}>
                <TableRow
                  hover
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {savedJob.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {savedJob.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {savedJob.patientName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                      {savedJob.useInsurance ? (
                        <HealthAndSafety fontSize="small" color="action" />
                      ) : (
                        <MoneyIcon fontSize="small" color="action" />
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {savedJob.useInsurance
                          ? 'Insurance:'
                          : 'Out of Pocket:'}{' '}
                        <br />
                        <strong>
                          {savedJob.useInsurance
                            ? savedJob.insuranceLabel
                            : savedJob.budgetRange}
                        </strong>
                      </Typography>
                    </Box>
                  </TableCell>
                  {/* <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="primary"
                    >
                      {savedJob.proposedRate}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {savedJob.servicePackageName}
                    </Typography>
                  </TableCell> */}
                  <TableCell align="center">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={0.5}
                    >
                      <PeopleIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {savedJob.applicantsCount}
                      </Typography>
                    </Box>
                  </TableCell>
                  {/* <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {savedJob.appliedDate}
                    </Typography>
                  </TableCell> */}
                  <TableCell align="center">
                    <Chip
                      label={savedJob.status}
                      color={getStatusColor(savedJob.status)}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <Tooltip placement="top" title="View job">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewJob(savedJob)}
                            disabled={!savedJob.jobId}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {/* <Tooltip placement="top" title="View submitted saved job">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDetailsDialog(savedJob)}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip> */}
                      <Tooltip placement="top" title="Message patient">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleSendMessageClick(savedJob)}
                        >
                          <MessageIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              </Grow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Grow>
  )

  return (
    <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={3}
        flexWrap="wrap"
        gap={2}
        width="100%"
      >
        {/* LEFT SIDE: Title + Description + Search */}
        <Box flex="1" minWidth="280px">
          <Typography
            variant="h4"
            gutterBottom
            color="primary"
            fontWeight={600}
          >
            Saved Jobs
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={2}>
            View and manage jobs you've saved for later
          </Typography>

          {/* SEARCH BAR JUST BELOW TITLE & DESCRIPTION */}
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: '100%', sm: '350px', md: '500px' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <Button
              variant="outlined"
              color="primary"
              onClick={clearFilters}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                py: 1,
                px: 2.5,
                width: { xs: '100%', sm: 'auto' },
                whiteSpace: 'nowrap',
              }}
            >
              Clear Filters
            </Button>
          </Box>
        </Box>

        {/* RIGHT SIDE: Toggle Buttons */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => newMode && setViewMode(newMode)}
          size="small"
        >
          <ToggleButton value="grid" aria-label="grid view">
            <ViewModuleIcon />
          </ToggleButton>

          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Tabs */}
      <Box mb={3}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 48,
            },
          }}
        >
          <Tab
            icon={<PendingIcon />}
            iconPosition="start"
            label={
              <Badge
                // badgeContent={proposals.filter(p => p.status === 'Pending').length}
                color="warning"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem' } }}
              >
                All
              </Badge>
            }
          />
          <Tab
            icon={<ConvertedIcon />}
            iconPosition="start"
            label={
              <Badge
                // badgeContent={proposals.filter(p => p.status === 'Pending').length}
                color="warning"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem' } }}
              >
                Active
              </Badge>
            }
          />
          <Tab
            icon={<RejectedIcon />}
            iconPosition="start"
            label={
              <Badge
                color="success"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem' } }}
              >
                Closed
              </Badge>
            }
          />
        </Tabs>
      </Box>

      {/* Content */}
      {loading ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="300px"
          gap={2}
        >
          <CircularProgress color="primary" />
          <Typography variant="body2" color="text.secondary">
            Loading saved jobs...
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : savedJobs.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          gap={2}
        >
          <Typography variant="h6" color="text.secondary">
            No saved Jobs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Save jobs to see them here.
          </Typography>
        </Box>
      ) : (
        <>
          <Box>
            {viewMode === 'grid' ? renderCardView() : renderTableView()}
          </Box>
          <Box mt={4} display="flex" justifyContent="flex-end">
            <AdvancedPagination
              count={totalRecords}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </>
      )}

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          sx={{ width: '100%' }}
        >
          {snackbarState.message || 'This feature is coming soon.'}
        </Alert>
      </Snackbar>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Saved Job Details</DialogTitle>
        <DialogContent dividers>
          {selectedSavedJob ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedSavedJob.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {selectedSavedJob.description}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2">Patient</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedSavedJob.patientName}
              </Typography>
              <Typography variant="subtitle2" mt={2}>
                Budget / Rate
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedSavedJob.budgetRange} — {selectedSavedJob.proposedRate}
              </Typography>
              {selectedSavedJob.insuranceLabel && (
                <>
                  <Typography variant="subtitle2" mt={2}>
                    Insurance
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedSavedJob.insuranceLabel}
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            <Typography variant="body2">No details available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog}>Close</Button>
          {selectedSavedJob?.jobId && (
            <Button
              onClick={() => handleViewJob(selectedSavedJob)}
              variant="contained"
            >
              View Job
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Chat Dialog */}
      {chatSavedJob &&
        (chatSavedJob as any).patientId &&
        (chatSavedJob as any).therapistId && (
          <ChatDialog
            open={chatDialogOpen}
            onClose={() => {
              setChatDialogOpen(false)
              setChatSavedJob(null)
            }}
            patientId={(chatSavedJob as any).patientId}
            therapistId={(chatSavedJob as any).therapistId}
            patientDisplayName={(chatSavedJob as any).patientName}
            patientAvatarUrl={(chatSavedJob as any).patientAvatar}
          />
        )}
    </Box>
  )
}

export default SavedJobsPage
